/**
 * Location Details Component
 *
 * Displays selected location with calendar, pickup times, and products.
 * Uses Tailwind CSS for styling (light DOM rendering).
 *
 * When a date is selected from the calendar, automatically selects the first
 * available (non-sold-out) time slot for that date.
 *
 * @element peach-location-details
 *
 * @fires date-selected - Emitted when user selects a calendar date (detail: { stop: Stop })
 * @fires timeslot-selected - Emitted when user selects a pickup time
 * @fires add-to-cart - Emitted when user adds product to cart
 * @fires close - Emitted when user closes the panel
 *
 * @example
 * ```html
 * <peach-location-details></peach-location-details>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import {
  fetchPreorderProducts,
  filterLocationProducts,
} from "@/services/products";
import type { Location, Stop } from "@/types/location";
import type { ShopifyProduct } from "@/types/shopify";
import { cn } from "@/utils/cn";
import { formatShortTimeRange } from "@/utils/date-time";
import { getStopStatusDisplay } from "@/utils/stop-status";
import {
  getLocationAvailability,
  type LocationAvailability,
} from "@/utils/location-availability";
import { getMapPageConfig } from "@/utils/map-config";
import "@/features/ui/button";
import "@/features/ui/icon";

// Import child components
import "./location-calendar";
import "./product-card";

@customElement("peach-location-details")
export class LocationDetailsComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Location to display (passed from parent)
   */
  @property({ type: Object })
  declare location: Location | null;

  /**
   * Currently selected stop (internal state)
   */
  @state()
  declare private selectedStop: Stop | null;

  /**
   * Selected timeslot ID
   */
  @state()
  declare private selectedTimeslotId: string | null;

  /**
   * Products for the current location
   */
  @state()
  declare private products: ShopifyProduct[];

  /**
   * Loading state for products
   */
  @state()
  declare private isLoadingProducts: boolean;

  constructor() {
    super();
    this.location = null;
    this.selectedStop = null;
    this.selectedTimeslotId = null;
    this.products = [];
    this.isLoadingProducts = false;
  }

  /**
   * Select a stop and update state
   */
  private selectStop(stop: Stop, source: string) {
    this.selectedStop = stop;
    this.selectedTimeslotId = String(stop.id);
    locationStore.getState().selectStop(stop.id);

    console.log(`[LocationDetails] ${source}:`, {
      date: stop.date,
      time: formatShortTimeRange(stop.start_time, stop.end_time),
    });
  }

  /**
   * Handle pre-selected stop from locationStore (e.g., URL restoration)
   */
  private handlePreselectedStop() {
    if (!this.location?.stops) return false;

    const mapState = locationStore.getState();
    if (mapState.selectedStopId === null) return false;

    const preselectedStop = this.location.stops.find(
      (s) => s.id === mapState.selectedStopId,
    );

    if (preselectedStop) {
      this.selectStop(preselectedStop, "Using pre-selected stop from locationStore");
      return true;
    }

    // Pre-selected stop not found in this location - fallback to first stop
    const firstStop = this.location.stops[0];
    this.selectStop(firstStop, "Pre-selected stop not found, using first stop");
    return true;
  }

  /**
   * Auto-select first available future stop when no pre-selection exists
   */
  private autoSelectFirstStop() {
    if (!this.location?.stops?.length) {
      this.selectedStop = null;
      this.selectedTimeslotId = null;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter to future stops only
    const futureStops = this.location.stops.filter((stop) => {
      const stopDate = new Date(stop.date);
      return stopDate >= today;
    });

    // Find first available (non-sold-out) future stop using full status logic
    const firstAvailable = futureStops.find((s) => getStopStatusDisplay(s).isAvailable);

    // Prefer: first available future > first future > first stop
    const selectedStop = firstAvailable ?? futureStops[0] ?? this.location.stops[0];
    this.selectStop(selectedStop, "Auto-selected first available future stop");
  }

  /**
   * Watch for property changes
   */
  updated(changedProperties: Map<string, any>) {
    // When location prop changes, reset related state
    if (!changedProperties.has("location")) return;

    console.log("[LocationDetails] Location prop changed:", this.location);

    // Try to handle pre-selected stop first, otherwise auto-select
    const wasPreselected = this.handlePreselectedStop();
    if (!wasPreselected) {
      this.autoSelectFirstStop();
    }

    // Fetch products for new location
    this.fetchProducts();
  }

  /**
   * Fetch products for the current location
   * Uses Liquid JSON endpoint pattern from legacy implementation
   */
  private async fetchProducts() {
    console.log("[LocationDetails] fetchProducts() called", {
      hasLocation: !!this.location,
      locationId: this.location?.id,
      locationName: this.location?.name,
      productIds: this.location?.product_ids,
      productIdsLength: this.location?.product_ids?.length,
    });

    if (!this.location) {
      console.log("[LocationDetails] No location, skipping");
      this.products = [];
      return;
    }

    if (!this.location.product_ids || this.location.product_ids.length === 0) {
      console.warn(
        "[LocationDetails] Location has no product_ids",
        this.location,
      );
      this.products = [];
      return;
    }

    this.isLoadingProducts = true;

    try {
      console.log(
        "[LocationDetails] Fetching from endpoint:",
        `/collections/all?view=${this.location.type === "Nashville" ? "nashville-" : ""}preorder-products.json`,
      );

      // Fetch all pre-order products from Liquid JSON endpoint
      const response = await fetchPreorderProducts(this.location.type);

      console.log("[LocationDetails] Response received:", {
        totalItems: response.items.length,
        firstItemTitle: response.items[0]?.title,
        firstItemId: response.items[0]?.id,
      });

      // Filter to only products available at this location
      const filteredProducts = filterLocationProducts(
        response.items,
        this.location.product_ids,
        undefined, // currentMarketId - can add if needed
        undefined, // excludableLocationIds - uses default
        this.location.id,
      );

      console.log("[LocationDetails] After filtering:", {
        filteredCount: filteredProducts.length,
        products: filteredProducts.map((p) => ({ id: p.id, title: p.title })),
      });

      this.products = filteredProducts;
    } catch (error) {
      console.error("[LocationDetails] Failed to fetch products:", error);
      this.products = [];
    } finally {
      this.isLoadingProducts = false;
    }
  }

  /**
   * Close the details panel
   */
  private handleClose() {
    locationStore.getState().clearSelection();

    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Find first available (non-sold-out) stop, or first stop if all sold out
   */
  private findFirstAvailableStop(stops: Stop[]): Stop {
    return stops.find((s: Stop) => getStopStatusDisplay(s).isAvailable) ?? stops[0];
  }

  /**
   * Handle date selection from calendar component
   */
  private handleDateSelected(event: CustomEvent) {
    const { stops } = event.detail;

    if (!stops?.length) return;

    const firstAvailableStop = this.findFirstAvailableStop(stops);

    // Update local state and store with the first available slot
    this.selectedStop = firstAvailableStop;
    this.selectedTimeslotId = String(firstAvailableStop.id);
    locationStore.getState().selectStop(firstAvailableStop.id);

    // Bubble the event up with the selected stop
    this.dispatchEvent(
      new CustomEvent("date-selected", {
        detail: { stop: firstAvailableStop },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Select a timeslot
   */
  private handleTimeslotClick(timeslotId: string) {
    this.selectedTimeslotId = timeslotId;

    // Update selectedStop to match the selected timeslot
    const stop = this.location?.stops.find((s) => String(s.id) === timeslotId);
    if (stop) {
      this.selectedStop = stop;
      locationStore.getState().selectStop(stop.id);
    }

    this.dispatchEvent(
      new CustomEvent("timeslot-selected", {
        detail: { timeslotId, stop },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Handle add to cart from product card component
   */
  private handleAddToCart(event: CustomEvent) {
    // Bubble the event up
    this.dispatchEvent(
      new CustomEvent("add-to-cart", {
        detail: event.detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Format date for display
   */
  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  /**
   * Render empty state when no location selected
   */
  private renderEmptyState() {
    return html`
      <div class="flex flex-col items-center justify-center h-full p-8 text-center bg-cream">
        <svg
          class="w-16 h-16 text-subtle mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <h3 class="text-default mb-2">
          Select a Location
        </h3>
        <p class="text-sm text-muted">
          Choose a location from the map or list to see details
        </p>
      </div>
    `;
  }

  /**
   * Render location header
   *
   * @remarks
   * On mobile the component lives inside a bottom sheet - the sheet's drag
   * handle provides swipe-to-dismiss, so we only need the X close button here.
   * On desktop the component lives in the sidebar panel alongside the map.
   */
  private renderHeader() {
    if (!this.location) return "";

    return html`
      <div class="bg-cream border-b border-default px-3 py-3 flex-shrink-0">
        <div class="flex items-center justify-between gap-2">
          <h2 class="leading-tight flex-1">
            ${this.location.name}
          </h2>

          <!-- Close button -->
          <peach-button variant="ghost-rounded" size="icon-sm" @click=${this.handleClose}>
            <peach-icon name="x-mark"></peach-icon>
          </peach-button>
        </div>
        <p class="text-xs leading-snug">${this.location.address}</p>
      </div>
    `;
  }

  /**
   * Render the season-complete / sold-out notice with optional email CTA
   *
   * @remarks
   * The signup invite and button only render when the section's signup
   * URL is configured, so the copy never invites a signup that isn't
   * there. (#314)
   */
  private renderAvailabilityNotice(availability: LocationAvailability) {
    if (availability === "active") return "";

    const isSeasonComplete = availability === "season-complete";
    const heading = isSeasonComplete
      ? "Season complete at this location"
      : "Sold out for the season";
    const message = isSeasonComplete
      ? "The Peach Truck has wrapped up its pickups here for this season. We hope to see you again next year!"
      : "All remaining pickup dates at this location are sold out.";
    const signupUrl = getMapPageConfig().signupUrl;

    return html`
      <div class="bg-surface-muted border-b border-default px-3 py-4">
        <h3 class="text-default mb-1">${heading}</h3>
        <p class="text-sm text-muted">${message}</p>
        ${signupUrl
          ? html`
            <p class="text-sm text-muted mt-2">
              Join our email list to be the first to know when new pickup
              dates are announced.
            </p>
            <a
              href=${signupUrl}
              class="inline-flex items-center justify-center rounded-md text-sm font-medium bg-brand-accent text-white h-9 px-4 py-2 mt-3"
            >
              Join the Email List
            </a>
          `
          : ""}
      </div>
    `;
  }

  /**
   * Render calendar section using extracted component
   */
  private renderCalendar() {
    if (!this.location) return "";

    return html`
      <peach-location-calendar
        .stops=${this.location.stops}
        .selectedStop=${this.selectedStop}
        @date-selected=${this.handleDateSelected}
      ></peach-location-calendar>
    `;
  }

  /**
   * Render timeslots section (Available Pickup Times)
   * Shows all time slots for the selected date
   */
  private renderTimeslots() {
    if (!this.selectedStop || !this.location) return "";

    // Find all stops for the selected date
    const selectedDate = this.selectedStop.date;
    const stopsForDate = this.location.stops.filter(
      (stop) => stop.date === selectedDate,
    );

    return html`
      <div class="bg-cream border-b border-default px-3 py-3">
        <h3 class="mb-2">
          Available Pickup Times
        </h3>
        <p class="text-xs text-muted mb-3">
          ${this.formatDate(selectedDate)}
        </p>

        <div class="space-y-2">
          ${stopsForDate.map((stop) => {
            const timeRange = formatShortTimeRange(
              stop.start_time,
              stop.end_time,
            );
            const status = getStopStatusDisplay(stop);
            const isSelected = this.selectedTimeslotId === String(stop.id);

            // Pre-compute button state conditions
            const isUnselectedAvailable = !isSelected && status.isAvailable;
            const isUnavailable = !status.isAvailable;

            return html`
              <button
                @click=${() => status.isAvailable && this.handleTimeslotClick(String(stop.id))}
                ?disabled=${!status.isAvailable}
                class=${cn(
                  "w-full px-3 py-2.5 border-2 rounded-lg text-left transition-all cursor-pointer",
                  {
                    // Selected state
                    "border-brand bg-surface-subtle": isSelected,

                    // Available state
                    "border-default hover:border-strong bg-surface hover:bg-surface-muted":
                      isUnselectedAvailable,

                    // Unavailable state
                    "border-default bg-surface-muted opacity-50 cursor-not-allowed":
                      isUnavailable,
                  },
                )}
              >
                <div class="flex items-center gap-2">
                  <div
                    class=${cn("w-2 h-2 rounded-full flex-shrink-0", {
                      "bg-brand-accent": status.isAvailable,
                      "bg-subtle": !status.isAvailable,
                    })}
                  ></div>
                  <div class="flex-1">
                    <div class="font-medium text-default text-sm">
                      ${timeRange}
                    </div>
                  </div>
                  ${
                    isSelected
                      ? html`
                      <peach-icon name="check-circle" class="text-brand"></peach-icon>
                    `
                      : ""
                  }
                </div>
              </button>
              ${isSelected && status.text !== "Available"
                ? html`
                  <div class=${cn(
                    "rounded-lg px-3 py-3",
                    {
                      "bg-warning-soft border-warning-ring": status.isAvailable,
                      "bg-error-soft border border-error": !status.isAvailable,
                    },
                  )}>
                    <div class="flex items-start gap-1.5">
                      ${status.subtext
                        ? html`<peach-icon name="walk-ups" class=${cn("mt-0.5", status.isAvailable ? "text-warning-text" : "text-error-text")} size="sm"></peach-icon>`
                        : ""
                      }
                      <div>
                        <p class=${cn("text-sm font-medium", {
                          "text-warning-text": status.isAvailable,
                          "text-error-text": !status.isAvailable,
                        })}>
                          ${status.text}
                        </p>
                        ${status.subtext
                          ? html`<p class="text-sm text-warning-text">${status.subtext}</p>`
                          : ""
                        }
                      </div>
                    </div>
                  </div>
                `
                : ""
              }
            `;
          })}
        </div>

        ${(() => {
          // Walk-up ONLY = all products have walk_up but NO preorder (checkout disabled)
          const isWalkUpOnly = (this.location!.products?.length ?? 0) > 0
            && this.location!.products!.every((p) => p.walk_up_available && !p.preorder_available);
          // All walk-up = all products have walk-up (any preorder status)
          const allWalkUp = (this.location!.products?.length ?? 0) > 0
            && this.location!.products!.every((p) => p.walk_up_available);
          const stopStatus = this.selectedStop
            ? getStopStatusDisplay(this.selectedStop, { isWalkUpOnly })
            : null;
          const checkoutDisabled = stopStatus?.checkoutDisabled ?? false;
          // Show walkup message for locations where ALL products have walkup + preorder
          // (per-product messages handle partial walkup; walk-up-only shows in status badge)
          return allWalkUp && !isWalkUpOnly && !checkoutDisabled
            ? html`
              <div class="bg-warning-soft border-warning-ring rounded-lg px-3 py-3 mt-3">
                <div class="flex items-center gap-1.5">
                  <peach-icon name="walk-ups" class="text-warning-text" size="sm"></peach-icon>
                  <p class="text-sm text-warning-text font-medium">Walk-ups available while supplies last</p>
                </div>
              </div>
            `
            : "";
        })()}
      </div>
    `;
  }

  /**
   * Render products skeleton loader
   */
  private renderProductsSkeleton() {
    return html`
      <div class="px-3 py-4">
        <!-- Product card skeletons -->
        ${[1, 2, 3].map(
          (_, i) => html`
            <div class="bg-surface rounded-lg border border-default p-3 mb-3 ${i === 2 ? "mb-0" : ""}">
              <div class="flex gap-3 mb-3">
                <div class="w-20 h-20 bg-skeleton rounded-lg animate-pulse flex-shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-3/4 bg-skeleton rounded-full animate-pulse"></div>
                  <div class="h-5 w-20 bg-skeleton rounded-full animate-pulse"></div>
                </div>
              </div>
              <div class="h-11 w-full bg-skeleton rounded-lg animate-pulse"></div>
            </div>
          `,
        )}
      </div>
    `;
  }

  /**
   * Render products section using extracted component
   */
  private renderProducts() {
    // Loading state - use skeleton loader
    if (this.isLoadingProducts) {
      return this.renderProductsSkeleton();
    }

    // No products available
    if (!this.products.length) {
      return html`
        <div class="px-3 py-4">
          <h3 class="text-default mb-2">
            Products
          </h3>
          <div class="bg-warning-soft border-warning-ring rounded-lg p-3 text-center">
            <svg class="w-10 h-10 text-warning-icon mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p class="text-sm text-muted">No products available for this location</p>
          </div>
        </div>
      `;
    }

    // Compute stop-level status for product rendering
    // Walk-up ONLY = all products have walk_up but NO preorder (checkout disabled)
    const isWalkUpOnly = (this.location!.products?.length ?? 0) > 0
      && this.location!.products!.every((p) => p.walk_up_available && !p.preorder_available);
    // All walk-up = all products have walk-up (for hiding per-product text when location-level shows)
    const allWalkUp = (this.location!.products?.length ?? 0) > 0
      && this.location!.products!.every((p) => p.walk_up_available);
    const stopStatus = this.selectedStop
      ? getStopStatusDisplay(this.selectedStop, { isWalkUpOnly })
      : null;
    const checkoutDisabled = stopStatus?.checkoutDisabled ?? false;

    // Render products using product-card component
    return html`
      <div class="px-3 py-4">
        <h3 class="text-default mb-3">
          ${this.products.length} Product${this.products.length !== 1 ? "s" : ""}
        </h3>

        <div class="flex flex-col gap-3">
          ${this.products.map(
            (product, index) => {
              const locationProduct = this.location!.products?.find(
                (p) => String(p.product_id) === String(product.id),
              );
              return html`
                <peach-product-card
                  .product=${product}
                  .location=${this.location!}
                  .selectedStop=${this.selectedStop!}
                  .last=${index === this.products.length - 1}
                  .checkoutDisabled=${checkoutDisabled}
                  .walkUpAvailable=${locationProduct?.walk_up_available ?? false}
                  .preorderAvailable=${locationProduct?.preorder_available ?? true}
                  .allWalkUp=${allWalkUp}
                  @add-to-cart=${this.handleAddToCart}
                ></peach-product-card>
              `;
            },
          )}
        </div>
      </div>
    `;
  }

  /**
   * Main render method
   */
  render() {
    if (!this.location) {
      return this.renderEmptyState();
    }

    const availability = getLocationAvailability(this.location);

    // Season-complete locations have only past dates, which would render
    // as bookable; show just the notice instead. Sold-out locations keep
    // the calendar so customers can see the sold-out dates.
    const showBookingUi = availability !== "season-complete";

    return html`
      <div class="flex flex-col flex-1 min-h-0 overflow-hidden bg-cream">
        ${this.renderHeader()}
        <div class="flex-1 min-h-0 overflow-y-auto">
          ${this.renderAvailabilityNotice(availability)}
          ${showBookingUi
            ? html`
              ${this.renderCalendar()}
              ${this.renderTimeslots()}
              ${this.renderProducts()}
            `
            : ""}
        </div>
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-location-details": LocationDetailsComponent;
  }
}
